/**
 * SocialChannelCard — eine wiederverwendbare Kanal-Karte mit konsistentem Zustand,
 * Badge, Button und ehrlichen Hinweisen. Bildet den aktuellen Stand 1:1 ab und
 * vereinfacht den Flow: pro Kanal genau EIN klarer Button, klare Labels, keine
 * Dev-Sprache, kein totes Disabled (Setup/Coming-Soon sind klickbar mit Hinweis).
 *
 * Drop-in: erwartet einen `channel` im Format der bestehenden PLATFORMS-Registry
 *   { id, label, description, icon, color, bgLight, oauthFlow, linkedTo?, comingSoon?, useWizard? }
 * und `accountData` aus /social-connect/status.
 */
import { CheckCircle, Loader2, AlertTriangle, Clock, Info, Activity, Trash2, ExternalLink, ListChecks, Lock } from "lucide-react";
import { getConnectButtonModel, getReconnectActionModel, getStatusBadge, STATUS } from "./channelButtonModel";
import { lieferModell, TON } from "./lieferModell";
import { useLanguage } from "@/lib/i18n/LanguageContext";

const TONE = {
  primary: "bg-fox-fire hover:bg-orange-600 text-white",
  amber:   "bg-amber-500 hover:bg-amber-600 text-white",
  violet:  "bg-violet-500 hover:bg-violet-600 text-white",
  green:   "bg-green-600 text-white",
  muted:   "bg-stone-100 text-stone-500",
};
const BADGE_TONE = {
  green:   "bg-green-100 text-green-600",
  amber:   "bg-amber-100 text-amber-600",
  red:     "bg-red-100 text-red-600",
  violet:  "bg-violet-100 text-violet-600",
  neutral: "bg-stone-100 text-stone-500",
};

/**
 * renderConnectButton — rendert den primären Aktions-Button eines Kanals.
 * @param channel   PLATFORMS-Eintrag
 * @param accountData  Status-Objekt
 * @param actions   { onConnect, onOpenWizard, onUpsell, connecting, access }
 */
export function renderConnectButton(channel, accountData, { onConnect, onOpenWizard, onUpsell, connecting, access } = {}) {
  const isConnecting = connecting === channel.id || accountData?.status === STATUS.CONNECTING;
  const model = getConnectButtonModel(channel, accountData, { isConnecting, access });

  if (model.kind === "connected") return null;            // Verbunden → Aktionen via renderConnectedActions
  if (model.kind === "linked") {
    return <p className="text-xs text-stone-400 flex-1 flex items-center">{model.hint}</p>;
  }

  const Icon = model.kind === "locked" ? Lock
    : model.kind === "coming_soon" ? Clock
    : model.kind === "wait" ? Clock
    : model.useWizard || model.assistant ? ListChecks
    : ExternalLink;

  const handleClick = () => {
    if (model.disabled) return;
    // Gesperrter Kanal: Klick löst Upsell aus (und wird geloggt) — kein Connect-Versuch.
    if (model.kind === "locked") return onUpsell?.(channel.id, model);
    if (model.useWizard && onOpenWizard) return onOpenWizard(channel.id);
    onConnect?.(channel.id);
  };

  return (
    <button
      onClick={handleClick}
      disabled={isConnecting || model.disabled}
      title={model.hint || undefined}
      data-testid={model.kind === "locked" ? `channel-locked-${channel.id}` : undefined}
      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${model.kind === "locked" ? "hover:bg-stone-200 border border-stone-200" : ""} ${TONE[model.tone] || TONE.primary}`}
    >
      {isConnecting ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />}
      {model.label}
    </button>
  );
}

function ConnectedActions({ channel, accountData, onRecover, onDisconnect, onConnect, onOpenWizard, connecting, access }) {
  const { t } = useLanguage();
  const busy = connecting === channel.id;
  // „Neu verbinden" auch im verbundenen Zustand — sonst führt der einzige Weg zu einem
  // frischen Zugang über „Trennen", und der löscht den noch gültigen Token, bevor der
  // neue existiert (Befund 14.08.2026, siehe getReconnectActionModel).
  const reconnect = getReconnectActionModel(channel, accountData, { access });
  return (
    <>
      <button
        onClick={() => onRecover?.(channel.id)}
        disabled={busy}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-stone-500 hover:bg-stone-100 transition-colors border border-stone-200 disabled:opacity-40"
        title={t("Verbindung prüfen", "Check connection")}
      >
        <Activity size={13} /> {t("Prüfen", "Check")}
      </button>
      {reconnect && (
        <button
          onClick={() => (reconnect.useWizard && onOpenWizard ? onOpenWizard(channel.id) : onConnect?.(channel.id))}
          disabled={busy}
          data-testid={`channel-reconnect-${channel.id}`}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-stone-500 hover:bg-stone-100 transition-colors border border-stone-200 disabled:opacity-40"
          title={t("Zugang erneuern, ohne die bestehende Verbindung vorher zu trennen", "Renew access without disconnecting the current connection first")}
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />} {reconnect.label}
        </button>
      )}
      <button
        onClick={() => onDisconnect?.(channel.id)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50 transition-colors border border-red-100"
        title={t("Verbindung trennen", "Disconnect")}
      >
        <Trash2 size={13} /> {t("Trennen", "Disconnect")}
      </button>
    </>
  );
}

export default function SocialChannelCard({ channel, accountData, onConnect, onRecover, onDisconnect, onOpenWizard, onUpsell, connecting, access, pendingReview }) {
  const { t } = useLanguage();
  const Icon = channel.icon;
  const status = accountData?.status || STATUS.NOT_CONNECTED;
  const connected = [STATUS.CONNECTED, STATUS.READY].includes(status);
  const badge = getStatusBadge(channel, accountData);
  const model = getConnectButtonModel(channel, accountData, { access });
  const displayName = accountData?.page_name || accountData?.profile_name || null;
  // „In Freischaltung": gekauft, technisch bereit, aber Betreiber-Freigabe (App-Review)
  // steht noch aus — ehrlicher Hinweis statt einer Fehlerseite beim Provider.
  const showPendingReview = !connected && pendingReview && model.kind !== "locked";
  // Urteil des Kanal-Wächters (läuft täglich 06:20). Beantwortet die Frage, die der
  // Verbindungsstatus NICHT beantwortet: „kommt wirklich an, was ich plane?"
  const lieferung = lieferModell(accountData);

  return (
    <div className={`bg-white rounded-xl border-2 p-5 transition-all ${connected ? "border-green-200" : "border-stone-200"}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${channel.color}`}>
            <Icon size={22} className="text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-stone-800 text-base">{channel.label}</h3>
            <p className="text-xs text-stone-400 mt-0.5 leading-snug">{channel.description}</p>
          </div>
        </div>
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${showPendingReview ? BADGE_TONE.violet : (BADGE_TONE[badge.tone] || BADGE_TONE.neutral)}`}>
          {showPendingReview
            ? <><Clock size={11} /> {t("In Freischaltung", "Pending approval")}</>
            : <>{badge.tone === "green" ? <CheckCircle size={11} /> : badge.tone === "red" ? <AlertTriangle size={11} /> : <div className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />}{badge.label}</>}
        </div>
      </div>

      {/* Verbundener Account */}
      {displayName && (
        <div className={`rounded-lg p-3 mb-3 ${channel.bgLight}`}>
          <p className="text-sm font-medium text-stone-700">{displayName}</p>
        </div>
      )}

      {/* Lieferurteil des Kanal-Wächters — nur wenn es etwas zu sagen gibt.
          Ein gesunder Kanal bekommt bewusst KEINEN Hinweis: eine Karte, die überall
          etwas anzeigt, trainiert das Auge zum Überlesen. Der Zustand vom 16.–20.08.
          (Verbindung tadellos, Beiträge online, jedes Bild fehlte) war hier bis dahin
          unsichtbar — der Verbindungsstatus meldete durchweg „verbunden". */}
      {connected && lieferung.zeigen && (
        <div
          className={`flex items-start gap-2 p-3 rounded-lg mb-3 border ${
            lieferung.ton === TON.STOERUNG
              ? "bg-red-50 border-red-200 text-red-800"
              : lieferung.ton === TON.HINWEIS
                ? "bg-amber-50 border-amber-200 text-amber-800"
                : "bg-stone-50 border-stone-200 text-stone-600"
          }`}
          data-testid={`channel-lieferung-${channel.id}`}
        >
          {lieferung.ton === TON.STOERUNG
            ? <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
            : <Info size={13} className="mt-0.5 flex-shrink-0" />}
          <div className="min-w-0">
            <p className="text-xs font-semibold leading-snug">{lieferung.titel}</p>
            {lieferung.grund && (
              <p className="text-xs leading-snug mt-0.5 opacity-90">{lieferung.grund}</p>
            )}
          </div>
        </div>
      )}

      {/* „In Freischaltung": gekauft, aber Betreiber-Freigabe (App-Review) läuft noch */}
      {showPendingReview && (
        <div className="flex items-start gap-2 p-3 rounded-lg mb-3 border bg-violet-50 border-violet-200 text-violet-700">
          <Clock size={13} className="mt-0.5 flex-shrink-0" />
          <p className="text-xs leading-snug">
            {t(
              "Dieser Kanal wird gerade freigeschaltet. Sobald die Prüfung der Plattform abgeschlossen ist, kannst du ihn verbinden — wir sagen dir Bescheid.",
              "This channel is currently being approved. Once the platform's review is complete, you'll be able to connect it — we'll let you know."
            )}
          </p>
        </div>
      )}

      {/* Ehrlicher Hinweis (Coming-Soon / Setup / Wizard / Gesperrt) */}
      {!connected && !showPendingReview && model.hint && model.kind !== "linked" && (
        <div className={`flex items-start gap-2 p-3 rounded-lg mb-3 border ${model.kind === "locked" ? "bg-stone-50 border-stone-200 text-stone-600" : model.tone === "violet" ? "bg-violet-50 border-violet-200 text-violet-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
          <Info size={13} className="mt-0.5 flex-shrink-0" />
          <p className="text-xs leading-snug">{model.hint}</p>
        </div>
      )}

      {/* Aktionen — genau EIN primärer Button (oder Prüfen/Trennen wenn verbunden) */}
      <div className="flex gap-2 mt-1">
        {connected
          ? <ConnectedActions channel={channel} accountData={accountData} onRecover={onRecover} onDisconnect={onDisconnect} onConnect={onConnect} onOpenWizard={onOpenWizard} connecting={connecting} access={access} />
          : renderConnectButton(channel, accountData, { onConnect, onOpenWizard, onUpsell, connecting, access })}
      </div>
    </div>
  );
}
